/**
 * Platform Layout
 *
 * Main layout for authenticated platform pages.
 * Command Center aesthetic with sidebar navigation.
 */
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from '@/components/platform/Toast';
import { platformLogout } from '@/lib/platform-api';
import { usePlatformAuthStore } from '@/stores/platform-auth';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  superAdminOnly?: boolean;
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/platform/dashboard',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    label: 'Organizations',
    path: '/platform/organizations',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16" />
        <path d="M1 21h22" />
        <path d="M9 7h1M9 11h1M9 15h1M14 7h1M14 11h1M14 15h1" />
      </svg>
    ),
  },
  {
    label: 'Provisioning',
    path: '/platform/provisioning',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
      </svg>
    ),
  },
  {
    label: 'Pricing',
    path: '/platform/pricing',
    superAdminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
      </svg>
    ),
  },
  {
    label: 'Promotions',
    path: '/platform/promotions',
    superAdminOnly: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
        <circle cx="9.5" cy="8.5" r=".5" fill="currentColor" />
        <circle cx="14.5" cy="13.5" r=".5" fill="currentColor" />
      </svg>
    ),
  },
  {
    label: 'Configuration',
    path: '/platform/config',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
      </svg>
    ),
  },
  {
    label: 'Audit Logs',
    path: '/platform/audit-logs',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
      </svg>
    ),
  },
];

export function PlatformLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, clearAuth } = usePlatformAuthStore();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = async () => {
    try {
      await platformLogout();
    } catch {
      // Ignore errors
    }
    clearAuth();
    navigate('/platform/login');
  };

  return (
    <div className="platform-layout">
      {/* Background grid */}
      <div className="platform-layout__grid" />

      {/* Sidebar */}
      <aside
        className={`platform-sidebar ${sidebarCollapsed ? 'platform-sidebar--collapsed' : ''}`}
      >
        {/* Logo */}
        <div className="platform-sidebar__header">
          <div className="platform-sidebar__logo">
            <svg viewBox="0 0 32 32" className="platform-sidebar__logo-icon">
              <rect x="3" y="3" width="11" height="11" fill="currentColor" opacity="0.9" />
              <rect x="18" y="3" width="11" height="11" fill="currentColor" opacity="0.6" />
              <rect x="3" y="18" width="11" height="11" fill="currentColor" opacity="0.6" />
              <rect x="18" y="18" width="11" height="11" fill="currentColor" opacity="0.3" />
            </svg>
            {!sidebarCollapsed && (
              <div className="platform-sidebar__logo-text">
                <span className="platform-sidebar__logo-name">PREGOO</span>
                <span className="platform-sidebar__logo-badge">CONTROL</span>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="platform-sidebar__toggle"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {sidebarCollapsed ? <path d="M9 18l6-6-6-6" /> : <path d="M15 18l-6-6 6-6" />}
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="platform-sidebar__nav">
          {navItems
            .filter((item) => !item.superAdminOnly || admin?.isSuperAdmin)
            .map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `platform-sidebar__link ${isActive ? 'platform-sidebar__link--active' : ''}`
                }
                title={sidebarCollapsed ? item.label : undefined}
              >
                <span className="platform-sidebar__link-icon">{item.icon}</span>
                {!sidebarCollapsed && (
                  <span className="platform-sidebar__link-label">{item.label}</span>
                )}
              </NavLink>
            ))}
        </nav>

        {/* Footer */}
        <div className="platform-sidebar__footer">
          {!sidebarCollapsed && (
            <div className="platform-sidebar__version">
              <span>v1.0.0</span>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className={`platform-main ${sidebarCollapsed ? 'platform-main--expanded' : ''}`}>
        {/* Header */}
        <header className="platform-header">
          <div className="platform-header__left">
            <h1 className="platform-header__title">
              {navItems.find((item) => location.pathname.startsWith(item.path))?.label ||
                'Platform'}
            </h1>
          </div>

          <div className="platform-header__right">
            {/* Status indicator */}
            <div className="platform-header__status">
              <span className="platform-header__status-dot" />
              <span>Online</span>
            </div>

            {/* User menu */}
            <div className="platform-header__user">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="platform-header__user-button"
              >
                <div className="platform-header__avatar">
                  {admin?.name?.charAt(0).toUpperCase() || 'A'}
                </div>
                {admin?.name && <span className="platform-header__user-name">{admin.name}</span>}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="platform-header__chevron"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {showUserMenu && (
                <>
                  <div
                    className="platform-header__menu-backdrop"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div className="platform-header__menu">
                    <div className="platform-header__menu-header">
                      <div className="platform-header__menu-name">{admin?.name}</div>
                      <div className="platform-header__menu-email">{admin?.email}</div>
                      {admin?.isSuperAdmin && (
                        <div className="platform-header__menu-badge">Super Admin</div>
                      )}
                    </div>
                    <div className="platform-header__menu-divider" />
                    <NavLink
                      to="/platform/settings"
                      className="platform-header__menu-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <span>Settings</span>
                    </NavLink>
                    <button
                      onClick={handleLogout}
                      className="platform-header__menu-item platform-header__menu-item--danger"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                        <path d="M16 17l5-5-5-5M21 12H9" />
                      </svg>
                      <span>Logout</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="platform-content">
          <Outlet />
        </main>
      </div>

      {/* Toast notifications */}
      <ToastContainer />

      <style>{`
        .platform-layout {
          min-height: 100vh;
          display: flex;
          background: #0A0F1C;
          position: relative;
        }

        .platform-layout__grid {
          position: fixed;
          inset: 0;
          background-image:
            linear-gradient(rgba(0, 229, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 229, 255, 0.02) 1px, transparent 1px);
          background-size: 40px 40px;
          pointer-events: none;
          z-index: 0;
        }

        /* Sidebar */
        .platform-sidebar {
          width: 240px;
          height: 100vh;
          position: fixed;
          left: 0;
          top: 0;
          z-index: 100;
          display: flex;
          flex-direction: column;
          background: rgba(10, 15, 28, 0.95);
          border-right: 1px solid rgba(0, 229, 255, 0.1);
          transition: width 0.2s ease;
        }

        .platform-sidebar--collapsed {
          width: 64px;
        }

        .platform-sidebar__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          border-bottom: 1px solid rgba(0, 229, 255, 0.1);
        }

        .platform-sidebar__logo {
          display: flex;
          align-items: center;
          gap: 12px;
          overflow: hidden;
        }

        .platform-sidebar__logo-icon {
          width: 32px;
          height: 32px;
          color: #00E5FF;
          flex-shrink: 0;
        }

        .platform-sidebar__logo-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .platform-sidebar__logo-name {
          font-family: 'JetBrains Mono', monospace;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 2px;
          color: #ffffff;
        }

        .platform-sidebar__logo-badge {
          font-family: 'JetBrains Mono', monospace;
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 1px;
          color: #00E5FF;
        }

        .platform-sidebar__toggle {
          padding: 6px;
          background: rgba(0, 229, 255, 0.1);
          border: 1px solid rgba(0, 229, 255, 0.2);
          border-radius: 4px;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .platform-sidebar__toggle:hover {
          background: rgba(0, 229, 255, 0.15);
          color: #00E5FF;
        }

        .platform-sidebar__toggle svg {
          width: 16px;
          height: 16px;
        }

        .platform-sidebar__nav {
          flex: 1;
          padding: 16px 8px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .platform-sidebar__link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border-radius: 4px;
          color: rgba(255, 255, 255, 0.6);
          text-decoration: none;
          transition: all 0.15s ease;
        }

        .platform-sidebar__link:hover {
          background: rgba(0, 229, 255, 0.08);
          color: rgba(255, 255, 255, 0.9);
        }

        .platform-sidebar__link--active {
          background: rgba(0, 229, 255, 0.12);
          color: #00E5FF;
        }

        .platform-sidebar__link--active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 24px;
          background: #00E5FF;
          border-radius: 0 2px 2px 0;
        }

        .platform-sidebar__link-icon {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        .platform-sidebar__link-icon svg {
          width: 100%;
          height: 100%;
        }

        .platform-sidebar__link-label {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
        }

        .platform-sidebar__footer {
          padding: 16px;
          border-top: 1px solid rgba(0, 229, 255, 0.1);
        }

        .platform-sidebar__version {
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          color: rgba(255, 255, 255, 0.3);
        }

        /* Main content */
        .platform-main {
          flex: 1;
          margin-left: 240px;
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          transition: margin-left 0.2s ease;
        }

        .platform-main--expanded {
          margin-left: 64px;
        }

        /* Header */
        .platform-header {
          position: sticky;
          top: 0;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 64px;
          padding: 0 24px;
          background: rgba(10, 15, 28, 0.9);
          border-bottom: 1px solid rgba(0, 229, 255, 0.1);
          backdrop-filter: blur(10px);
        }

        .platform-header__left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .platform-header__title {
          font-family: 'DM Sans', sans-serif;
          font-size: 20px;
          font-weight: 600;
          color: #ffffff;
          margin: 0;
        }

        .platform-header__right {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .platform-header__status {
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          color: rgba(255, 255, 255, 0.5);
        }

        .platform-header__status-dot {
          width: 8px;
          height: 8px;
          background: #10B981;
          border-radius: 50%;
          box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
        }

        .platform-header__user {
          position: relative;
        }

        .platform-header__user-button {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 12px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .platform-header__user-button:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.15);
        }

        .platform-header__avatar {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #00E5FF 0%, #00B8D4 100%);
          border-radius: 4px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: #0A0F1C;
        }

        .platform-header__user-name {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
        }

        .platform-header__chevron {
          width: 16px;
          height: 16px;
          color: rgba(255, 255, 255, 0.4);
        }

        .platform-header__menu-backdrop {
          position: fixed;
          inset: 0;
          z-index: 40;
        }

        .platform-header__menu {
          position: absolute;
          right: 0;
          top: calc(100% + 8px);
          width: 240px;
          background: rgba(15, 23, 42, 0.98);
          border: 1px solid rgba(0, 229, 255, 0.15);
          border-radius: 6px;
          overflow: hidden;
          z-index: 50;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
        }

        .platform-header__menu-header {
          padding: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .platform-header__menu-name {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
        }

        .platform-header__menu-email {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          margin-top: 2px;
        }

        .platform-header__menu-badge {
          display: inline-block;
          margin-top: 8px;
          padding: 4px 8px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.5px;
          color: #00E5FF;
          background: rgba(0, 229, 255, 0.1);
          border: 1px solid rgba(0, 229, 255, 0.2);
          border-radius: 2px;
        }

        .platform-header__menu-divider {
          height: 1px;
          background: rgba(255, 255, 255, 0.1);
        }

        .platform-header__menu-item {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.7);
          background: none;
          border: none;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.15s ease;
          text-align: left;
        }

        .platform-header__menu-item:hover {
          background: rgba(255, 255, 255, 0.05);
          color: #ffffff;
        }

        .platform-header__menu-item--danger:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #f87171;
        }

        .platform-header__menu-item svg {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }

        /* Content */
        .platform-content {
          flex: 1;
          padding: 24px;
        }

        @media (max-width: 768px) {
          .platform-sidebar {
            transform: translateX(-100%);
          }

          .platform-sidebar--open {
            transform: translateX(0);
          }

          .platform-main {
            margin-left: 0;
          }

          .platform-header__user-name {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}

export default PlatformLayout;
